import { Command } from 'commander';
import { ContextBuilder } from '../core/context/context-builder';
import { ContextOptimizer } from '../core/context/context-optimizer';
import { ContextCompressor } from '../core/context/context-compressor';
import { DynamicToolCaller } from '../core/context/tool-caller';
import { ServiceRegistry } from '../core/service-registry';
import { ConfigManager } from '../core/config-manager';
import { EnhancedTaskAnalyzer } from '../core/enhanced-task-analyzer';
import { logger } from '../utils/logger';
import chalk from 'chalk';
import { Table } from 'console-table-printer';
import inquirer from 'inquirer';
import ora from 'ora';

// 创建上下文命令
export function createContextCommand(): Command {
  const context = new Command('context')
    .description('Context-aware commands leveraging AI context engineering');

  const serviceRegistry = new ServiceRegistry();
  const configManager = new ConfigManager();
  const contextBuilder = new ContextBuilder(serviceRegistry);
  const contextOptimizer = new ContextOptimizer();
  const contextCompressor = new ContextCompressor();
  const toolCaller = new DynamicToolCaller(serviceRegistry);
  const taskAnalyzer = new EnhancedTaskAnalyzer();

  // 显示当前上下文
  context
    .command('show')
    .description('Show current context information')
    .option('-d, --detailed', 'Show detailed context')
    .option('-s, --stats', 'Show context statistics')
    .action(async (options) => {
      const spinner = ora('Building current context...').start();
      
      try {
        // 构建当前上下文
        const currentContext = await contextBuilder.build({
          metadata: {
            source: 'cli',
            command: 'context show'
          }
        });

        spinner.succeed('Context built successfully');

        // 显示基本信息
        console.log(chalk.cyan('\n📋 Current Context:'));
        console.log(chalk.gray(`  ID: ${currentContext.id}`));
        console.log(chalk.gray(`  Created: ${new Date(currentContext.metadata.timestamp).toLocaleString()}`));
        console.log(chalk.gray(`  Source: ${currentContext.metadata.source}`));

        if (currentContext.task) {
          console.log(chalk.cyan('\n📌 Active Task:'));
          console.log(chalk.gray(`  Description: ${currentContext.task.description || 'N/A'}`));
          console.log(chalk.gray(`  Type: ${currentContext.task.type || 'N/A'}`));
        }

        if (currentContext.workflow) {
          console.log(chalk.cyan('\n🔄 Active Workflow:'));
          console.log(chalk.gray(`  Template: ${currentContext.workflow.templateId}`));
          console.log(chalk.gray(`  Status: ${currentContext.workflow.status}`));
        }

        // 显示知识摘要
        if (currentContext.knowledge && currentContext.knowledge.length > 0) {
          console.log(chalk.cyan('\n📚 Knowledge Base:'));
          console.log(chalk.gray(`  ${currentContext.knowledge.length} relevant items found`));
          
          if (options.detailed) {
            currentContext.knowledge.slice(0, 5).forEach((item, index) => {
              console.log(chalk.yellow(`\n  ${index + 1}. ${item.title || 'Untitled'}`));
              console.log(chalk.gray(`     Type: ${item.type}`));
              if (item.content) {
                const preview = item.content.substring(0, 100) + '...';
                console.log(chalk.gray(`     ${preview}`));
              }
            });
          }
        }

        // 显示可用工具
        if (currentContext.tools && currentContext.tools.length > 0) {
          console.log(chalk.cyan('\n🛠️  Available Tools:'));
          const toolGroups = currentContext.tools.reduce((acc, tool) => {
            const category = tool.metadata?.category || 'other';
            if (!acc[category]) acc[category] = [];
            acc[category].push(tool);
            return acc;
          }, {} as Record<string, typeof currentContext.tools>);

          Object.entries(toolGroups).forEach(([category, tools]) => {
            console.log(chalk.yellow(`  ${category}:`));
            tools.forEach(tool => {
              console.log(chalk.gray(`    - ${tool.name}`));
            });
          });
        }

        // 显示历史记录摘要
        if (currentContext.history && currentContext.history.length > 0) {
          console.log(chalk.cyan('\n📜 Recent History:'));
          console.log(chalk.gray(`  ${currentContext.history.length} items in context`));
        }

        // 显示统计信息
        if (options.stats) {
          console.log(chalk.cyan('\n📊 Context Statistics:'));
          
          const stats = {
            totalSize: JSON.stringify(currentContext).length,
            knowledgeItems: currentContext.knowledge?.length || 0,
            toolsAvailable: currentContext.tools?.length || 0,
            historyItems: currentContext.history?.length || 0,
            optimizationPotential: 0
          };

          // 计算优化潜力
          const analysis = await contextOptimizer.analyze(currentContext);
          stats.optimizationPotential = Math.round((1 - analysis.efficiency) * 100);

          const table = new Table({
            columns: [
              { name: 'metric', alignment: 'left' },
              { name: 'value', alignment: 'right' }
            ]
          });

          table.addRow({ metric: 'Total Size', value: `${(stats.totalSize / 1024).toFixed(2)} KB` });
          table.addRow({ metric: 'Knowledge Items', value: stats.knowledgeItems });
          table.addRow({ metric: 'Available Tools', value: stats.toolsAvailable });
          table.addRow({ metric: 'History Items', value: stats.historyItems });
          table.addRow({ 
            metric: 'Optimization Potential', 
            value: chalk[stats.optimizationPotential > 30 ? 'red' : 'green'](`${stats.optimizationPotential}%`) 
          });

          table.printTable();
        }

      } catch (error) {
        spinner.fail(`Failed to build context: ${error.message}`);
        logger.error('Context show error:', error);
      }
    });

  // 为任务构建上下文
  context
    .command('build <task>')
    .description('Build optimized context for a specific task')
    .option('-o, --optimize', 'Apply context optimization')
    .option('-c, --compress', 'Apply context compression')
    .option('-s, --save <path>', 'Save context to file')
    .action(async (task, options) => {
      const spinner = ora('Analyzing task...').start();
      
      try {
        // 分析任务
        spinner.text = 'Analyzing task requirements...';
        const analysisResult = await taskAnalyzer.analyzeTask({
          title: task,
          description: task
        });

        // 构建上下文
        spinner.text = 'Building context...';
        let builtContext = await contextBuilder.build({
          task: {
            description: task,
            type: analysisResult.type,
            complexity: analysisResult.complexity
          },
          query: task
        });

        // 优化上下文
        if (options.optimize) {
          spinner.text = 'Optimizing context...';
          builtContext = await contextOptimizer.optimize(builtContext, {
            strategy: 'hybrid',
            constraints: {
              maxSize: 50000,
              maxItems: 100
            }
          });
        }

        // 压缩上下文
        if (options.compress) {
          spinner.text = 'Compressing context...';
          builtContext = await contextCompressor.compress(builtContext, {
            strategy: 'adaptive',
            targetRatio: 0.5
          });
        }

        spinner.succeed('Context built successfully');

        // 显示结果
        console.log(chalk.cyan('\n✅ Context Built:'));
        console.log(chalk.gray(`  Task Type: ${analysisResult.type}`));
        console.log(chalk.gray(`  Complexity: ${analysisResult.complexity.overall}/10`));
        console.log(chalk.gray(`  Context Size: ${(JSON.stringify(builtContext).length / 1024).toFixed(2)} KB`));

        // 显示上下文内容摘要
        console.log(chalk.cyan('\n📋 Context Summary:'));
        
        if (builtContext.knowledge && builtContext.knowledge.length > 0) {
          console.log(chalk.yellow(`  Knowledge: ${builtContext.knowledge.length} relevant items`));
        }
        
        if (builtContext.workflow) {
          console.log(chalk.yellow(`  Workflow: ${builtContext.workflow.templateId}`));
        }
        
        if (builtContext.tools && builtContext.tools.length > 0) {
          console.log(chalk.yellow(`  Tools: ${builtContext.tools.length} available`));
          const topTools = builtContext.tools.slice(0, 5).map(t => t.name).join(', ');
          console.log(chalk.gray(`    Top tools: ${topTools}`));
        }

        // 显示 AI 提示词
        if (analysisResult.aiPrompt) {
          console.log(chalk.cyan('\n🤖 AI Prompt:'));
          console.log(chalk.gray(analysisResult.aiPrompt));
        }

        // 保存到文件
        if (options.save) {
          const fs = await import('fs/promises');
          await fs.writeFile(options.save, JSON.stringify(builtContext, null, 2));
          console.log(chalk.green(`\n✓ Context saved to: ${options.save}`));
        }

      } catch (error) {
        spinner.fail(`Failed to build context: ${error.message}`);
        logger.error('Context build error:', error);
      }
    });

  // 基于上下文推荐操作
  context
    .command('suggest')
    .description('Get AI-powered suggestions based on current context')
    .option('-t, --type <type>', 'Suggestion type (tools, knowledge, workflow)')
    .option('-l, --limit <n>', 'Number of suggestions', '5')
    .action(async (options) => {
      const spinner = ora('Analyzing context...').start();
      
      try {
        // 构建当前上下文
        const currentContext = await contextBuilder.build({});
        
        spinner.text = 'Generating suggestions...';

        const suggestions: any[] = [];

        // 基于上下文推荐工具
        if (!options.type || options.type === 'tools') {
          const recommendedTools = await recommendTools(currentContext);
          suggestions.push({
            type: 'tools',
            title: '🛠️  Recommended Tools',
            items: recommendedTools.slice(0, parseInt(options.limit))
          });
        }

        // 基于上下文推荐知识
        if (!options.type || options.type === 'knowledge') {
          const recommendedKnowledge = await recommendKnowledge(currentContext);
          suggestions.push({
            type: 'knowledge',
            title: '📚 Relevant Knowledge',
            items: recommendedKnowledge.slice(0, parseInt(options.limit))
          });
        }

        // 基于上下文推荐工作流
        if (!options.type || options.type === 'workflow') {
          const recommendedWorkflows = await recommendWorkflowsFromContext(currentContext);
          suggestions.push({
            type: 'workflow',
            title: '🔄 Suggested Workflows',
            items: recommendedWorkflows.slice(0, parseInt(options.limit))
          });
        }

        spinner.succeed('Suggestions generated');

        // 显示建议
        suggestions.forEach(category => {
          console.log(chalk.cyan(`\n${category.title}:`));
          
          category.items.forEach((item: any, index: number) => {
            console.log(chalk.yellow(`\n  ${index + 1}. ${item.name || item.title}`));
            if (item.description) {
              console.log(chalk.gray(`     ${item.description}`));
            }
            if (item.reason) {
              console.log(chalk.gray(`     Why: ${item.reason}`));
            }
            if (item.score) {
              console.log(chalk.gray(`     Relevance: ${Math.round(item.score * 100)}%`));
            }
          });
        });

      } catch (error) {
        spinner.fail(`Failed to generate suggestions: ${error.message}`);
        logger.error('Context suggest error:', error);
      }
    });

  // AI 辅助命令
  context
    .command('ai <prompt>')
    .description('Execute AI-assisted commands based on context')
    .option('-e, --execute', 'Execute suggested actions')
    .option('-i, --interactive', 'Interactive mode')
    .action(async (prompt, options) => {
      const spinner = ora('Processing AI request...').start();
      
      try {
        // 构建增强的上下文
        spinner.text = 'Building enhanced context...';
        const enhancedContext = await contextBuilder.build({
          query: prompt,
          metadata: {
            aiAssisted: true,
            prompt
          }
        });

        // 分析意图
        spinner.text = 'Analyzing intent...';
        const intent = await analyzeIntent(prompt, enhancedContext);
        
        spinner.succeed('Analysis complete');

        console.log(chalk.cyan('\n🤖 AI Analysis:'));
        console.log(chalk.gray(`  Intent: ${intent.type}`));
        console.log(chalk.gray(`  Confidence: ${Math.round(intent.confidence * 100)}%`));

        // 推荐工具调用
        if (intent.suggestedTools && intent.suggestedTools.length > 0) {
          console.log(chalk.cyan('\n🛠️  Suggested Tools:'));
          
          const toolCalls = intent.suggestedTools.map((tool: any) => ({
            tool: tool.name,
            parameters: tool.parameters,
            reason: tool.reason
          }));

          toolCalls.forEach((call, index) => {
            console.log(chalk.yellow(`\n  ${index + 1}. ${call.tool}`));
            console.log(chalk.gray(`     Parameters: ${JSON.stringify(call.parameters)}`));
            console.log(chalk.gray(`     Reason: ${call.reason}`));
          });

          // 执行或交互
          if (options.execute) {
            console.log(chalk.cyan('\n🚀 Executing tools...'));
            
            for (const call of toolCalls) {
              try {
                const result = await toolCaller.callTool(call.tool, call.parameters, enhancedContext);
                console.log(chalk.green(`\n✓ ${call.tool} executed successfully`));
                if (result) {
                  console.log(chalk.gray(`  Result: ${JSON.stringify(result).substring(0, 100)}...`));
                }
              } catch (error) {
                console.log(chalk.red(`✗ ${call.tool} failed: ${error.message}`));
              }
            }
          } else if (options.interactive) {
            const answer = await inquirer.prompt([
              {
                type: 'checkbox',
                name: 'tools',
                message: 'Select tools to execute:',
                choices: toolCalls.map((call, index) => ({
                  name: `${call.tool} - ${call.reason}`,
                  value: index,
                  checked: true
                }))
              }
            ]);

            for (const index of answer.tools) {
              const call = toolCalls[index];
              try {
                const result = await toolCaller.callTool(call.tool, call.parameters, enhancedContext);
                console.log(chalk.green(`\n✓ ${call.tool} executed successfully`));
              } catch (error) {
                console.log(chalk.red(`✗ ${call.tool} failed: ${error.message}`));
              }
            }
          }
        }

        // 显示建议的工作流
        if (intent.suggestedWorkflow) {
          console.log(chalk.cyan('\n🔄 Suggested Workflow:'));
          console.log(chalk.yellow(`  ${intent.suggestedWorkflow.name}`));
          console.log(chalk.gray(`  ${intent.suggestedWorkflow.description}`));
          
          const startWorkflow = await inquirer.prompt([
            {
              type: 'confirm',
              name: 'start',
              message: 'Start this workflow?',
              default: false
            }
          ]);

          if (startWorkflow.start) {
            console.log(chalk.green('Starting workflow...'));
            // TODO: 实际启动工作流
          }
        }

      } catch (error) {
        spinner.fail(`AI processing failed: ${error.message}`);
        logger.error('AI command error:', error);
      }
    });

  return context;
}

// 推荐工具
async function recommendTools(context: any): Promise<any[]> {
  const tools = context.tools || [];
  
  // 基于任务类型和上下文推荐工具
  return tools.map((tool: any) => {
    let score = 0.5; // 基础分数
    
    // 根据任务类型调整分数
    if (context.task?.type === 'bug' && tool.metadata?.category === 'debugging') {
      score += 0.3;
    } else if (context.task?.type === 'feature' && tool.metadata?.category === 'development') {
      score += 0.3;
    }
    
    // 根据历史使用频率调整
    if (tool.metadata?.usageCount > 10) {
      score += 0.1;
    }
    
    return {
      ...tool,
      score,
      reason: getToolRecommendationReason(tool, context)
    };
  }).sort((a, b) => b.score - a.score);
}

// 推荐知识
async function recommendKnowledge(context: any): Promise<any[]> {
  const knowledge = context.knowledge || [];
  
  return knowledge.map((item: any) => ({
    ...item,
    score: item.metadata?.relevance || 0.5,
    reason: `Relevant to ${context.task?.type || 'current task'}`
  })).sort((a, b) => b.score - a.score);
}

// 基于上下文推荐工作流
async function recommendWorkflowsFromContext(context: any): Promise<any[]> {
  // TODO: 实现基于上下文的工作流推荐
  return [];
}

// 分析意图
async function analyzeIntent(prompt: string, context: any): Promise<any> {
  const lowerPrompt = prompt.toLowerCase();
  
  // 简单的意图识别
  let type = 'general';
  let confidence = 0.5;
  const suggestedTools = [];
  
  if (lowerPrompt.includes('fix') || lowerPrompt.includes('bug')) {
    type = 'bugfix';
    confidence = 0.8;
    suggestedTools.push({
      name: 'search',
      parameters: { query: 'error' },
      reason: 'Find related error messages'
    });
  } else if (lowerPrompt.includes('test')) {
    type = 'testing';
    confidence = 0.8;
    suggestedTools.push({
      name: 'test',
      parameters: {},
      reason: 'Run test suite'
    });
  } else if (lowerPrompt.includes('deploy') || lowerPrompt.includes('release')) {
    type = 'deployment';
    confidence = 0.8;
  }
  
  return {
    type,
    confidence,
    suggestedTools,
    prompt
  };
}

// 获取工具推荐原因
function getToolRecommendationReason(tool: any, context: any): string {
  const reasons = [];
  
  if (context.task?.type === 'bug' && tool.metadata?.category === 'debugging') {
    reasons.push('Useful for debugging');
  }
  
  if (tool.metadata?.usageCount > 10) {
    reasons.push('Frequently used');
  }
  
  if (context.history?.some((h: any) => h.tool === tool.name)) {
    reasons.push('Recently used');
  }
  
  return reasons.join(', ') || 'May be helpful for this task';
}